/**
 * Bundle upsell — "Add Both to Cart".
 *
 * Posts ONE request with an items array rather than two sequential adds. Two
 * adds race the cart-drawer refresh: the second response can render a drawer
 * built from the cart as it was before the first finished, and the customer
 * sees one of the two items missing.
 *
 * The discount itself is never computed here. It is an automatic discount in
 * Shopify; this only says what is about to happen and lets Shopify be right.
 */
class BundleUpsell extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-bundle-add]');
    this.selects = Array.from(this.querySelectorAll('[data-bundle-variant]'));
    this.errorEl = this.querySelector('[data-bundle-error]');
    this.cart =
      document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    this.button?.addEventListener('click', this.onClick.bind(this));
  }

  get items() {
    return this.selects
      .map((select) => ({ id: select.value, quantity: 1 }))
      .filter((item) => item.id);
  }

  setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }

  onClick(event) {
    event.preventDefault();
    if (this.button.getAttribute('aria-disabled') === 'true') return;

    const missing = this.selects.find((select) => !select.value);
    if (missing) {
      this.setError(this.dataset.sizeError || 'Please choose a size for each item');
      missing.focus();
      return;
    }

    this.setError('');
    this.button.setAttribute('aria-disabled', 'true');
    this.button.classList.add('loading');

    const body = { items: this.items };
    if (this.cart) {
      body.sections = this.cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      this.cart.setActiveElement(document.activeElement);
    }

    fetch(routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.setError(response.description || response.message);
          return;
        }

        if (!this.cart) {
          window.location = routes.cart_url;
          return;
        }

        this.cart.renderContents(response);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-upsell', cartData: response });
      })
      .catch(() => this.setError(this.dataset.genericError || 'Something went wrong. Please try again.'))
      .finally(() => {
        this.button.removeAttribute('aria-disabled');
        this.button.classList.remove('loading');
      });
  }
}

if (!customElements.get('bundle-upsell')) {
  customElements.define('bundle-upsell', BundleUpsell);
}
